import React, { useCallback, useMemo } from "react";
import { Control, UseFormMethods } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useGetDealersForSelect } from "~/entity/Dealers";
import { EmployeeService } from "~/entity/Employee/api/service";
import { EmployeeDocumentType } from "~/entity/Employee/model/schema";
import { useGetPartnersForSelect } from "~/entity/Partners/hooks/useGetPartnersForSelect";
import { useGetPositionsForSelect } from "~/hooks/useGetPositionsForSelect";
import { getShortDate } from "~/libs/getFormatDate";
import {
  getEducationCertificateOptions,
  getEducationTrainingOptions,
} from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/options";
import { canRequestTrainingLicenses } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/trainingLicense";
import {
  IEmployeeProfileForm,
  TSelectOption,
} from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/types";
import { useCardEditAccess } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/useCardEditAccess";
import { openStaffEmployeeWindow } from "~/shared/lib/createURL";

import AppointmentFieldsRow from "./AppointmentFieldsRow";
import EducationSelectField from "./EducationSelectField";
import HiddenDealerControllers from "./HiddenDealerControllers";
import OrganizationFieldsRow from "./OrganizationFieldsRow";
import PositionHeader from "./PositionHeader";
import PositionSelectField from "./PositionSelectField";
import StatusFieldsRow from "./StatusFieldsRow";
import { TDealerErrors, TDealerFieldPrefix } from "./types";

import s from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/EmployeeProfileForm.less";

interface IPositionItemProps {
  index: number;
  positionNumber: string;
  control: Control<IEmployeeProfileForm>;
  errors: UseFormMethods<IEmployeeProfileForm>["errors"];
  setValue: UseFormMethods<IEmployeeProfileForm>["setValue"];
  watch: UseFormMethods<IEmployeeProfileForm>["watch"];
  licensesLoaded: boolean;
  onRequestLicense: () => void;
  onRemove?: () => void;
  isNew?: boolean;
  refetch?: () => void;
}

const PositionItem = ({
  index,
  positionNumber: positionNumberProp,
  control,
  errors,
  setValue,
  watch,
  licensesLoaded,
  onRequestLicense,
  onRemove,
  isNew: isNewPosition = false,
  refetch,
}: IPositionItemProps) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const positionData = watch(
    `positions.${index}`,
  ) as IEmployeeProfileForm["positions"][number];
  const positionNumber = positionNumberProp || positionData?.number || "";
  const isMain = positionNumber === "one";

  const { canEditAll, canEditAny } = useCardEditAccess();
  const canRequestLicenses = canRequestTrainingLicenses();

  const { intersectionObserver, searchText, positions } =
    useGetPositionsForSelect(true);
  const {
    options: dealers,
    onScrollBottom: onScrollBottomDealers,
    onSearch: searchDealer,
  } = useGetDealersForSelect(true, { pageSize: 100, immediate: true });

  const positionValue = positionData?.position;
  const dealerData = positionData?.dealers?.[0];
  const educationTraining = positionData?.educationTraining;
  const licenseAssigned = positionData?.licenseAssigned ?? false;
  const licenseFreeCount = positionData?.licenseFreeCount ?? 0;

  const organizationType =
    dealerData?.organizationType === "PARTNER" ? "PARTNER" : "DEALER";
  const { options: partners, onSearch: searchPartner } =
    useGetPartnersForSelect(organizationType === "PARTNER");

  const isVerifiedPosition =
    !isNewPosition && dealerData?.status === "verified";
  const disabledField = isVerifiedPosition || !canEditAll;
  const disabledPartialField = isVerifiedPosition || !canEditAny;

  const isFiredDealer = Boolean(
    dealerData?.endDate &&
    (dealerData?.endOrderNumber || "").trim() &&
    dealerData?.dismissalOrderId,
  );
  const positionOrderRequired = !isVerifiedPosition && !isFiredDealer;

  const emailRequired =
    (isMain || Boolean(positionValue?.value)) &&
    dealerData?.status !== "verified";

  const showDismissalFile = !isMain || Boolean(dealerData?.employeePositionId);
  const hasDismissalData =
    Boolean(dealerData?.endDate) ||
    Boolean((dealerData?.endOrderNumber || "").trim());
  const dismissalOrderRequired = hasDismissalData && showDismissalFile;

  const dealerPrefix: TDealerFieldPrefix = `positions.${index}.dealers.0`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dealerErrors = (errors.positions as any)?.[index]
    ?.dealers?.[0] as TDealerErrors;

  const uploadPositionDocument = useCallback(
    (type: EmployeeDocumentType) => (file: File) => {
      if (!dealerData?.employeePositionId) {
        return Promise.resolve(undefined);
      }
      return EmployeeService.POST.uploadEmployeeDocument(
        file,
        type,
        String(dealerData.employeePositionId),
      ).then(({ data }) => ({
        id: String(data.fileId),
        link: data.link,
        name: data.name,
      }));
    },
    [dealerData?.employeePositionId],
  );

  const sectionTitle = isMain
    ? t("employee.positionMainTitle")
    : t("employee.positionSecondaryTitle");

  const handlePositionChange = (value: TSelectOption) => {
    setValue(`positions.${index}.position`, value);
    setValue(`positions.${index}.licenseAssigned`, false);
    setValue(`positions.${index}.licenseFreeCount`, 0);
  };

  const openTrainingLink = useCallback(() => {
    if (!positionValue?.value || !id || id === "add") {
      return;
    }

    const filterItems = (positionData?.dealers || [])
      .map((item) => item.dealer)
      .filter((dealer): dealer is { value: string; label: string } =>
        Boolean(dealer?.value),
      );

    openStaffEmployeeWindow({
      position: positionValue.label,
      positionIds: [Number(positionValue.value)],
      filterBy: "DEALER",
      period: getShortDate(new Date()),
      filterItems,
      employeeIds: id,
    });
  }, [id, positionData?.dealers, positionValue]);

  const educationTrainingOptions = useMemo(
    () => getEducationTrainingOptions(t),
    [t],
  );
  const educationCertificateOptions = useMemo(
    () => getEducationCertificateOptions(t),
    [t],
  );

  const dealerRows = (
    <>
      <OrganizationFieldsRow
        control={control}
        dealerPrefix={dealerPrefix}
        dealerErrors={dealerErrors}
        disabled={disabledPartialField}
        organizationDisabled={disabledField}
        dealers={organizationType === "PARTNER" ? partners : dealers}
        onSearchDealer={
          organizationType === "PARTNER" ? searchPartner : searchDealer
        }
        onScrollBottomDealers={
          organizationType === "PARTNER" ? undefined : onScrollBottomDealers
        }
        onOrganizationTypeChange={() =>
          setValue(`${dealerPrefix}.dealer`, null)
        }
        consentFile={dealerData?.dataConsent}
        emailRequired={emailRequired}
      />
      <StatusFieldsRow
        control={control}
        dealerPrefix={dealerPrefix}
        employeeId={id}
        isNewPosition={isNewPosition}
        refetch={refetch}
        dealerData={dealerData}
        directionRole={positionData?.directionRole}
        directionId={positionData?.directionId}
        disabled={disabledPartialField}
        showJobDescription={id !== "add"}
        uploadJobDescription={
          dealerData?.employeePositionId
            ? uploadPositionDocument("JOB_DESCRIPTION")
            : undefined
        }
      />
      <AppointmentFieldsRow
        control={control}
        dealerPrefix={dealerPrefix}
        dealerErrors={dealerErrors}
        disabled={disabledPartialField}
        positionOrderRequired={positionOrderRequired}
        positionOrderFile={dealerData?.positionOrder}
      />
      {/* TODO PASDILER-2428: поля приказа на увольнение временно скрыты до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
      <DismissalFieldsRow
        control={control}
        dealerPrefix={dealerPrefix}
        dealerErrors={dealerErrors}
        endDateDisabled={!canEditAny}
        orderNumberDisabled={!canEditAny}
        fileDisabled={!canEditAny}
        showDismissalFile={showDismissalFile}
        dismissalOrderRequired={dismissalOrderRequired}
        uploadDismissalOrder={
          dealerData?.employeePositionId
            ? uploadPositionDocument("DISMISSAL_ORDER")
            : undefined
        }
        dismissalOrderFile={dealerData?.dismissalOrder}
      /> */}
    </>
  );

  return (
    <div className={s.positionBlock}>
      <HiddenDealerControllers
        index={index}
        positionNumber={positionNumberProp}
        control={control}
        dealerPrefix={dealerPrefix}
      />
      <PositionHeader
        title={sectionTitle}
        onRemove={!isMain ? onRemove : undefined}
      />

      {isMain ? (
        <div className={s.grid4}>
          <PositionSelectField
            index={index}
            control={control}
            options={positions}
            onSearch={searchText}
            onPopupScroll={intersectionObserver}
            disabled={disabledField}
            onPositionChange={handlePositionChange}
          />
          <EducationSelectField
            name={`positions.${index}.educationTraining`}
            control={control}
            label={t("employee.EducationTraining")}
            options={educationTrainingOptions}
            onOpenLink={openTrainingLink}
          />
          <EducationSelectField
            name={`positions.${index}.educationCertificate`}
            control={control}
            label={t("employee.EducationCertificate")}
            options={educationCertificateOptions}
            onOpenLink={openTrainingLink}
          />
          {/* TODO PASDILER-2428: свитчер лицензии для обучения временно скрыт до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
          <TrainingLicenseControl
            positionId={positionValue?.value}
            educationTraining={educationTraining}
            licenseAssigned={licenseAssigned}
            licenseFreeCount={licenseFreeCount}
            licensesLoaded={licensesLoaded}
            canRequestLicenses={canRequestLicenses}
            onRequestLicense={onRequestLicense}
          /> */}
        </div>
      ) : (
        <div className={s.grid4}>
          <PositionSelectField
            index={index}
            control={control}
            options={positions}
            onSearch={searchText}
            onPopupScroll={intersectionObserver}
            disabled={disabledField}
            allowClear
            placeholder={t("employee.noPosition")}
            onPositionChange={handlePositionChange}
          />
        </div>
      )}

      {dealerRows}
    </div>
  );
};

export default PositionItem;
